// Pure helpers for the in-app QR scanner. The scanner is only an INPUT
// mechanism: it turns a decoded QR string into a token and hands it to the
// existing /scan/:qrToken route. It never authorises anything itself.

const TOKEN = /^[A-Za-z0-9_-]{1,128}$/;

// Only the configured public origin is accepted. When none is configured
// (local/staging fallback), the origin this app is served from is.
export function allowedScanOrigin(publicBaseUrl) {
    try {
        return new URL(publicBaseUrl || window.location.origin).origin;
    } catch (e) {
        return window.location.origin;
    }
}

// Returns the qrToken for a valid Anike scan URL (`<public url>/scan/<token>`),
// or null for anything else (other sites, plain text, Wi-Fi/contact codes,
// malformed or non-http(s) URLs).
export function parseAnikeScanUrl(text, publicBaseUrl) {
    if (typeof text !== "string") return null;
    let url;
    try {
        url = new URL(text.trim());
    } catch (e) {
        return null;
    }
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    if (url.origin !== allowedScanOrigin(publicBaseUrl)) return null;

    const match = url.pathname.match(/^\/scan\/([^/]+)\/?$/);
    if (!match) return null;
    let token;
    try {
        token = decodeURIComponent(match[1]);
    } catch (e) {
        return null;
    }
    return TOKEN.test(token) ? token : null;
}

// The in-app route a scan resolves to once the backend has authorised it.
export const assetRouteFor = ({ _id, access }) =>
    access === "assigned" ? `/asset/maintenance/${_id}` : `/asset/view/asset/${_id}`;

// Shared by the /scan/:qrToken page and the in-app workflow scanner so a
// failed scan reads the same everywhere. Deliberately generic where
// specificity would reveal whether an asset exists to someone not entitled
// to know (unknown / other organisation / not assigned all look the same).
export const SCAN_FAILURE_MESSAGES = {
    NOT_FOUND: {
        title: "QR code not recognised",
        text: "This QR code isn't recognised, or you don't have access to the asset it points to."
    },
    QR_RETIRED: {
        title: "This QR code is no longer active",
        text: "Please use the latest label for this asset."
    },
    ASSET_REMOVED: {
        title: "This asset has been removed",
        text: "It is no longer available."
    },
    NO_PERMISSION: {
        title: "Access denied",
        text: "Your role doesn't have access to assets. Contact your organisation administrator."
    },
    ERROR: {
        title: "Something went wrong",
        text: "We couldn't process this QR code. Please try again."
    }
};

export const scanFailureCode = error => {
    const status = error && error.response && error.response.status;
    const code = error && error.response && error.response.data && error.response.data.code;
    if (code && SCAN_FAILURE_MESSAGES[code]) return code;
    if (status === 404) return "NOT_FOUND";
    if (status === 403) return "NO_PERMISSION";
    return "ERROR";
};

// The asset context a workflow receives after a successful, server-authorised
// scan. Built field-by-field so nothing else the API might return (and never
// a qrToken) can leak into workflow state, route state or the URL.
export const toSelectedAsset = data => ({
    _id: data._id,
    assetID: data.assetID || "",
    name: data.name || "",
    access: data.access || "full",
    ownerId: data.ownerId || null,
    site: data.site ? { _id: data.site._id, name: data.site.name } : null,
    locationName: data.locationName || null,
    responsibleTechnician: data.responsibleTechnician
        ? {
              _id: data.responsibleTechnician._id,
              firstName: data.responsibleTechnician.firstName,
              lastName: data.responsibleTechnician.lastName
          }
        : null
});

const technicianName = rt => [rt.firstName, rt.lastName].filter(Boolean).join(" ");

// Route-state shapes the existing Service Request / Work Order / Report
// Fault forms already pre-fill from (see Assets/ViewAsset.js assetStateRoute
// and Assets/ViewAssignedAsset.js setFaultRoute) - reused as-is, so scanning
// feeds the same code path as clicking "Raise SR/WO" on Asset 360.
export const toAssetDetailsState = asset => ({
    _id: asset._id,
    assetID: asset.assetID,
    name: `${asset.name} - ${asset.assetID}`,
    responsibleTechnician: asset.responsibleTechnician
        ? { ...asset.responsibleTechnician, name: technicianName(asset.responsibleTechnician) }
        : null
});

export const toFaultDetailsState = asset => ({
    _id: asset._id,
    assetID: asset.assetID,
    name: asset.name,
    responsibleTechnician: asset.responsibleTechnician
        ? { ...asset.responsibleTechnician, name: technicianName(asset.responsibleTechnician) }
        : null
});
